import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as topojsonClient from "topojson-client";
import { geoMercator, geoPath } from "d3-geo";
import useMapZoom from "@/hooks/useMapZoom";
import MapZoomControls from "@/components/MapZoomControls";

// Map topojson state names → canonical names used by our app / routes
const TOPO_TO_APP = {
  "Andaman & Nicobar Island": "Andaman & Nicobar Islands",
  "Arunanchal Pradesh": "Arunachal Pradesh",
  "Dadara & Nagar Havelli": "Dadra & Nagar Haveli and Daman & Diu",
  "Daman & Diu": "Dadra & Nagar Haveli and Daman & Diu",
  "NCT of Delhi": "Delhi",
  "Orissa": "Odisha",
};
const canonical = (name) => (name ? TOPO_TO_APP[name] || name : null);

/**
 * StateMap — draws only the boundary of a single state and overlays
 * markers for each hotel (blue = operational, orange = upcoming).
 * Hotels without lat/lng are shown as a badge in the top-left.
 */
export default function StateMap({ stateName, hotels = [], height = 520 }) {
  const [topo, setTopo] = useState(null);
  const containerRef = useRef(null);
  const [width, setWidth] = useState(700);
  const [hoveredHotel, setHoveredHotel] = useState(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();
  const zoomCtl = useMapZoom({ width, height });

  useEffect(() => {
    fetch("/india-states.json").then((r) => r.json()).then(setTopo).catch(() => setTopo(null));
  }, []);

  useEffect(() => {
    const update = () => containerRef.current && setWidth(containerRef.current.clientWidth);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const { paths, projection } = useMemo(() => {
    if (!topo) return { paths: [], projection: null };
    const objectKey = Object.keys(topo.objects)[0];
    const geo = topojsonClient.feature(topo, topo.objects[objectKey]);
    const stateFeatures = geo.features.filter((f) => canonical(f.properties?.name) === stateName);
    if (stateFeatures.length === 0) return { paths: [], projection: null };

    // Build a combined feature set: state polygon(s) + hotel points → ensures fit includes all markers
    const hotelPointFeatures = (hotels || [])
      .filter(
        (h) =>
          typeof h.latitude === "number" &&
          typeof h.longitude === "number" &&
          Number.isFinite(h.latitude) &&
          Number.isFinite(h.longitude)
      )
      .map((h) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [h.longitude, h.latitude] },
        properties: { hotelId: h.id },
      }));

    const combined = {
      type: "FeatureCollection",
      features: [...stateFeatures, ...hotelPointFeatures],
    };
    const proj = geoMercator().fitExtent(
      [[32, 32], [width - 32, height - 32]],
      combined
    );
    const pathGen = geoPath(proj);
    return {
      paths: stateFeatures.map((f, i) => ({ d: pathGen(f), key: i })),
      projection: proj,
    };
  }, [topo, stateName, hotels, width, height]);

  // Group hotels by city — one marker per city (matches India-map clustering).
  const cityGroups = useMemo(() => {
    const map = new Map();
    for (const h of hotels) {
      const lat = h.latitude, lng = h.longitude;
      if (typeof lat !== "number" || typeof lng !== "number") continue;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const key = h.city;
      const g = map.get(key) || {
        city: h.city,
        latSum: 0,
        lngSum: 0,
        hotels: [],
        operational: 0,
        upcoming: 0,
      };
      g.latSum += lat;
      g.lngSum += lng;
      g.hotels.push(h);
      if (h.status === "upcoming") g.upcoming += 1;
      else g.operational += 1;
      map.set(key, g);
    }
    // Compute average centre per city
    return [...map.values()].map((g) => ({
      ...g,
      lat: g.latSum / g.hotels.length,
      lng: g.lngSum / g.hotels.length,
    }));
  }, [hotels]);

  const missingCoords = hotels.filter((h) => typeof h.latitude !== "number" || typeof h.longitude !== "number");

  return (
    <div
      ref={containerRef}
      data-testid={`state-map-${slug(stateName)}`}
      className="relative w-full bg-[var(--paper-2)] border border-[var(--ink)] touch-none"
      style={{ minHeight: height, cursor: zoomCtl.zoom > 1 ? "grab" : "default" }}
      onMouseMove={(e) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }}
      {...zoomCtl.pointerHandlers}
    >
      {!topo ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="font-label text-xs text-[var(--ink-soft)]">Loading map…</div>
        </div>
      ) : paths.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="font-label text-xs text-[var(--ink-soft)]">Map for “{stateName}” not available.</div>
        </div>
      ) : (
        <svg viewBox={zoomCtl.viewBox} width="100%" height={height}>
          {paths.map((p) => (
            <path key={p.key} d={p.d} fill="#F3F4F6" stroke="#111827" strokeWidth={1.2} />
          ))}
          {projection &&
            cityGroups.map((g, i) => {
              const [x, y] = projection([g.lng, g.lat]) || [0, 0];
              const mixed = g.operational > 0 && g.upcoming > 0;
              const primary = g.operational >= g.upcoming ? "#0047FF" : "#FF5A36";
              const secondary = g.operational >= g.upcoming ? "#FF5A36" : "#0047FF";
              const r = g.hotels.length === 1 ? 10 : Math.min(20, 12 + Math.log2(g.hotels.length) * 3);
              return (
                <g
                  key={i}
                  style={{ cursor: "pointer" }}
                  data-testid={`state-map-marker-${slug(g.city)}`}
                  onMouseEnter={() => setHoveredHotel(g)}
                  onMouseLeave={() => setHoveredHotel(null)}
                  onClick={() => { if (!zoomCtl.suppressClick()) navigate(`/state/${encodeURIComponent(stateName)}/city/${encodeURIComponent(g.city)}`); }}
                >
                  {/* Invisible large touch hit area */}
                  <circle cx={x} cy={y} r={Math.max(r + 12, 22)} fill="transparent" />
                  {/* Soft halo */}
                  <circle cx={x} cy={y} r={r + 6} fill={primary} fillOpacity={0.14} />
                  {mixed ? (
                    <>
                      <path
                        d={`M ${x} ${y - r} A ${r} ${r} 0 0 0 ${x} ${y + r} Z`}
                        fill={primary}
                      />
                      <path
                        d={`M ${x} ${y - r} A ${r} ${r} 0 0 1 ${x} ${y + r} Z`}
                        fill={secondary}
                      />
                      <circle cx={x} cy={y} r={r} fill="none" stroke="white" strokeWidth={2.5} />
                    </>
                  ) : (
                    <circle cx={x} cy={y} r={r} fill={primary} stroke="white" strokeWidth={2.5} />
                  )}
                  {g.hotels.length > 1 && (
                    <text
                      x={x}
                      y={y + 4}
                      textAnchor="middle"
                      fontSize={r >= 16 ? 12 : 11}
                      fontWeight={700}
                      fill="white"
                      pointerEvents="none"
                      style={{ fontFamily: "Space Grotesk, sans-serif" }}
                    >
                      {g.hotels.length}
                    </text>
                  )}
                </g>
              );
            })}
        </svg>
      )}

      <MapZoomControls
        zoom={zoomCtl.zoom}
        onZoomIn={zoomCtl.zoomIn}
        onZoomOut={zoomCtl.zoomOut}
        onReset={zoomCtl.reset}
        testIdPrefix="state-map-zoom"
      />

      {hoveredHotel && (
        <div
          data-testid="state-map-tooltip"
          className="pointer-events-none absolute bg-[var(--ink)] text-white px-4 py-3 border border-[var(--ink)] z-20 shadow-[6px_6px_0_0_var(--brand-blue)]"
          style={{
            left: Math.min(cursor.x + 16, (containerRef.current?.clientWidth || 700) - 240),
            top: Math.max(cursor.y - 60, 8),
          }}
        >
          <div className="font-label text-[10px] text-[var(--brand-yellow)] mb-1">City</div>
          <div className="font-display text-lg leading-none">{hoveredHotel.city}</div>
          <div className="text-xs mt-2 text-white/80">
            {hoveredHotel.hotels.length} {hoveredHotel.hotels.length === 1 ? "hotel" : "hotels"}
          </div>
          <div className="text-xs mt-1 text-white/70 flex items-center gap-3 flex-wrap">
            {hoveredHotel.operational > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-blue)]" />
                {hoveredHotel.operational} operational
              </span>
            )}
            {hoveredHotel.upcoming > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-orange)]" />
                {hoveredHotel.upcoming} upcoming
              </span>
            )}
          </div>
        </div>
      )}

      {missingCoords.length > 0 && (
        <div className="absolute top-3 left-3 bg-white border border-[var(--ink)] px-3 py-2 font-label text-[10px]">
          {missingCoords.length} hotel{missingCoords.length > 1 ? "s" : ""} without coordinates
        </div>
      )}

      <div className="absolute bottom-3 right-3 bg-white border border-[var(--ink)] p-2 sm:p-3 flex items-center gap-3 sm:gap-4 text-[10px] font-label">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--brand-blue)]" />
          <span>Operational</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--brand-orange)]" />
          <span>Upcoming</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ background: "linear-gradient(90deg, #0047FF 50%, #FF5A36 50%)" }}
          />
          <span>Mixed</span>
        </div>
      </div>
    </div>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
